import { GAMMA_MEDIUM_CHAIN } from "./gamma";
import { summariseDailyQuality } from "./hourly-windows";
// DOWNWIND FOIL scoring v1.
//
// Downwind foiling is point-to-point gliding on open-ocean bumps. It wants
// what prone foiling fears: real sea state (1.5-3m of combined swell and
// wind waves is prime) and moderate wind to generate and sustain the bumps
// (15-25 km/h is beneficial, not a defect). Wind chop is not a penalty -
// wind waves ARE the ride, so they feed the energy base instead of a chop
// factor. Period matters far less than for any wave-riding sport: short
// period wind swell is the canonical downwind bump.
//
// Architecture matches the platform convention: a multiplicative penalty
// chain compressed with adaptive gamma (MEDIUM_CHAIN - 8 factors), then a
// best-window daily blend. Safety indicators are computed independently and
// never modify the score; storms are the flag layer's job.

import type { SunTimes } from "./daylight";
import { isDark } from "./daylight";
import { composeScore, defineChain } from "./compose-score";
import { REVISION_ONE_FORMULA, type LeafFormula } from "./engines/core/formula";
import { classifySuppliedCurrent } from "./seaward-current";
import { classifyWindAgainstTide, windAgainstTideDetail } from "./wind-against-tide";
import { classifyModelFog } from "./model-fog";

// Downwind foil's chain, declared as data (tracker P3-19); classes declared, not enforced (Phase 7).
export const DOWNWIND_FOIL_CHAIN = defineChain({
  sport: "downwind-foil",
  gamma: GAMMA_MEDIUM_CHAIN, // Pinned calibration; factor-list refactors must not change the scale.
  factors: [
    { key: "base", class: "driver", note: "value is base/100" },
    { key: "period", class: "driver" },
    { key: "windSpeed", class: "driver" },
    { key: "alignment", class: "driver", note: "P7-2 fixes the opposed-swell inversion; class stays driver" },
    { key: "gust", class: "driver" },
    { key: "current", class: "driver", note: "opposing and cross-run effort when current direction is known; magnitude fallback is partial evidence" },
    { key: "rain", class: "modifier", note: "floor 0.65 breaches 0.85; P7-3" },
    { key: "visibility", class: "driver", note: "documented tier migration: navigation over kilometres degrades the run itself (05 B1); kept deep" },
  ],
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * Math.max(0, Math.min(1, t));
}

function angleDiff(a: number, b: number): number {
  const d = Math.abs((((a - b) % 360) + 360) % 360);
  return d > 180 ? 360 - d : d;
}

// ---------------------------------------------------------------------------
// Named thresholds & constants
// ---------------------------------------------------------------------------

// Energy base: combined sea state (sqrt(swell^2 + windwave^2)).
const SEA_DEAD_FLAT_M = 0.1;
const SEA_MARGINAL_M = 0.3;
const SEA_RIDEABLE_M = 0.5;
const SEA_BUILDING_M = 0.8;
const SEA_GOOD_M = 1.2;
const SEA_PRIME_START_M = 1.5;
const SEA_PRIME_PEAK_M = 2.0;
const SEA_PRIME_END_M = 3.0;
const SEA_SERIOUS_M = 3.5;
const SEA_HEAVY_M = 4.5;
const SEA_EXTREME_M = 6.0;
const SEA_FLOOR = 15;

// Wind: bump generation. Glass is still good when the bumps already exist
// (height is measured directly), so the low end floors high.
const WIND_GLASS_KMH = 5;
const WIND_LIGHT_KMH = 12;
const WIND_BENEFIT_START_KMH = 15;
const WIND_BENEFIT_END_KMH = 25;
const WIND_FRESH_KMH = 32;
const WIND_STRONG_KMH = 40;
const WIND_SEVERE_KMH = 50;
const WIND_STORM_KMH = 60;
const WIND_GLASS_FACTOR = 0.90;
const WIND_LIGHT_FACTOR = 0.95;
const WIND_FRESH_FACTOR = 0.92;
const WIND_STRONG_FACTOR = 0.70;
const WIND_SEVERE_FACTOR = 0.45;
const WIND_STORM_FACTOR = 0.25;
const WIND_FLOOR = 0.20;

// Wind/swell alignment: wind blowing with the swell builds organised bump
// trains; wind against the swell makes confused, collapsing seas.
const ALIGN_GATE_KMH = 12;
const ALIGN_GOOD_DEG = 60;
const ALIGN_CROSS_DEG = 120;
const ALIGN_CROSS_FACTOR = 0.85;
// P7-2 (audit 05 V1): a dead-opposed dominant swell collapses the bump train
// into confused, doubling-up seas with the glide largely gone. Deepened from
// 0.70 to 0.35 so a swell-dominated opposed sea reads <= 50, NOT Firing. The
// penalty is scaled by the swell's share of combined energy (downwindSwellEnergyShare)
// so a small background swell crossing a dominant wind sea is barely dented (05 B4),
// and opposed swell separately loses its energy-base credit (downwindSwellRideWeight)
// so adding opposed swell can never raise the score.
const ALIGN_OPPOSED_FACTOR = 0.35;

// Gusts: downwind runs read the water ahead; gust lulls collapse glide but
// the consequence is a paddle, not a lofting. Gentler than prone.
const GUST_GATE_KMH = 12;
const GUST_RATIO_OK = 1.5;
const GUST_RATIO_HIGH = 2.0;
const GUST_PENALTY_MODERATE = 0.88;
const GUST_PENALTY_HEAVY = 0.85;

// Current: open-water runs care less than spot-bound sports. Mostly a
// logistics factor until it gets genuinely strong.
const CURRENT_SAFE_MS = 0.4;
const CURRENT_NOTICEABLE_MS = 0.8;
const CURRENT_STRONG_MS = 1.5;
const CURRENT_NOTICEABLE_FACTOR = 0.85;
const CURRENT_STRONG_FACTOR = 0.60;
const CURRENT_FLOOR = 0.55;

const RAIN_LIGHT_MM = 2;
const RAIN_MODERATE_MM = 5;
const RAIN_HEAVY_MM = 10;
const RAIN_FLOOR = 0.65;

// Visibility: runs finish kilometres from the start; seeing the coastline
// and other water users matters more than at a fixed spot.
const VIS_CLEAR_M = 5000;
const VIS_REDUCED_M = 2000;
const VIS_POOR_M = 1000;
const VIS_DANGEROUS_M = 500;
const VIS_FLOOR = 0.35;

const DAILY_BEST_WINDOW_WEIGHT = 0.65;
const DAILY_BEST_WINDOW_HOURS = 3;

// ---------------------------------------------------------------------------
// Factor 1: Energy base (0–100) - combined sea state
//
// Rewards exactly the band the prone model punishes: 1.5-3m of open-ocean
// energy is prime, and the curve declines gently above it (big days are
// expert days, not write-offs).
// ---------------------------------------------------------------------------

export function downwindEnergyBase(combinedSeaM: number): number {
  const h = combinedSeaM;
  if (h <= SEA_DEAD_FLAT_M) return 0;
  if (h <= SEA_MARGINAL_M) return lerp(0, 8, (h - SEA_DEAD_FLAT_M) / (SEA_MARGINAL_M - SEA_DEAD_FLAT_M));
  if (h <= SEA_RIDEABLE_M) return lerp(8, 25, (h - SEA_MARGINAL_M) / (SEA_RIDEABLE_M - SEA_MARGINAL_M));
  if (h <= SEA_BUILDING_M) return lerp(25, 50, (h - SEA_RIDEABLE_M) / (SEA_BUILDING_M - SEA_RIDEABLE_M));
  if (h <= SEA_GOOD_M) return lerp(50, 78, (h - SEA_BUILDING_M) / (SEA_GOOD_M - SEA_BUILDING_M));
  if (h <= SEA_PRIME_START_M) return lerp(78, 92, (h - SEA_GOOD_M) / (SEA_PRIME_START_M - SEA_GOOD_M));
  if (h <= SEA_PRIME_PEAK_M) return lerp(92, 100, (h - SEA_PRIME_START_M) / (SEA_PRIME_PEAK_M - SEA_PRIME_START_M));
  if (h <= SEA_PRIME_END_M) return 100;
  if (h <= SEA_SERIOUS_M) return lerp(100, 88, (h - SEA_PRIME_END_M) / (SEA_SERIOUS_M - SEA_PRIME_END_M));
  if (h <= SEA_HEAVY_M) return lerp(88, 60, (h - SEA_SERIOUS_M) / (SEA_HEAVY_M - SEA_SERIOUS_M));
  if (h <= SEA_EXTREME_M) return lerp(60, 30, (h - SEA_HEAVY_M) / (SEA_EXTREME_M - SEA_HEAVY_M));
  return SEA_FLOOR + (30 - SEA_FLOOR) * Math.exp(-(h - SEA_EXTREME_M) / 2);
}

// ---------------------------------------------------------------------------
// Factor 2: Period (0.92–1.02) - nearly flat
//
// Short-period wind swell IS the downwind bump. Longer-period energy glides
// slightly better, but the spread is a few percent, not a band.
// ---------------------------------------------------------------------------

export function downwindPeriodFactor(period: number): number {
  if (period <= 4) return 0.92;
  if (period <= 8) return lerp(0.92, 0.99, (period - 4) / 4);
  if (period <= 11) return lerp(0.99, 1.02, (period - 8) / 3);
  return 1.02;
}

// ---------------------------------------------------------------------------
// Factor 3: Wind speed (0.20–1.0) - moderate wind is beneficial
// ---------------------------------------------------------------------------

export function downwindWindSpeedFactor(speed: number): number {
  if (speed <= WIND_GLASS_KMH) return WIND_GLASS_FACTOR;
  if (speed <= WIND_LIGHT_KMH) return lerp(WIND_GLASS_FACTOR, WIND_LIGHT_FACTOR, (speed - WIND_GLASS_KMH) / (WIND_LIGHT_KMH - WIND_GLASS_KMH));
  if (speed <= WIND_BENEFIT_START_KMH) return lerp(WIND_LIGHT_FACTOR, 1.0, (speed - WIND_LIGHT_KMH) / (WIND_BENEFIT_START_KMH - WIND_LIGHT_KMH));
  if (speed <= WIND_BENEFIT_END_KMH) return 1.0;
  if (speed <= WIND_FRESH_KMH) return lerp(1.0, WIND_FRESH_FACTOR, (speed - WIND_BENEFIT_END_KMH) / (WIND_FRESH_KMH - WIND_BENEFIT_END_KMH));
  if (speed <= WIND_STRONG_KMH) return lerp(WIND_FRESH_FACTOR, WIND_STRONG_FACTOR, (speed - WIND_FRESH_KMH) / (WIND_STRONG_KMH - WIND_FRESH_KMH));
  if (speed <= WIND_SEVERE_KMH) return lerp(WIND_STRONG_FACTOR, WIND_SEVERE_FACTOR, (speed - WIND_STRONG_KMH) / (WIND_SEVERE_KMH - WIND_STRONG_KMH));
  if (speed <= WIND_STORM_KMH) return lerp(WIND_SEVERE_FACTOR, WIND_STORM_FACTOR, (speed - WIND_SEVERE_KMH) / (WIND_STORM_KMH - WIND_SEVERE_KMH));
  return WIND_FLOOR;
}

// ---------------------------------------------------------------------------
// Factor 4: Wind/swell alignment (0.70–1.0)
//
// Gated on wind strong enough to shape the sea. Unknown directions are
// neutral, never a penalty.
// ---------------------------------------------------------------------------

export function downwindAlignmentFactor(
  windSpeed: number,
  windDirection: number | null,
  swellDirection: number | null,
): number {
  if (windSpeed < ALIGN_GATE_KMH) return 1.0;
  if (windDirection === null || swellDirection === null) return 1.0;
  // Wind direction is "from", swell direction is "from": aligned systems
  // share the same origin direction.
  const diff = angleDiff(windDirection, swellDirection);
  if (diff <= ALIGN_GOOD_DEG) return 1.0;
  if (diff <= ALIGN_CROSS_DEG) return lerp(1.0, ALIGN_CROSS_FACTOR, (diff - ALIGN_GOOD_DEG) / (ALIGN_CROSS_DEG - ALIGN_GOOD_DEG));
  return lerp(ALIGN_CROSS_FACTOR, ALIGN_OPPOSED_FACTOR, (diff - ALIGN_CROSS_DEG) / (180 - ALIGN_CROSS_DEG));
}

// Swell-energy ride weight (P7-2, audit 05 V1). Opposed swell does not build a
// rideable downwind bump train, so it must not add to the energy base - otherwise
// adding opposed swell mass raises the score (the V1 inversion). Aligned and
// cross swell (diff <= 120 deg) are fully rideable; an opposed swell ramps its
// rideable-energy credit to zero by 180 deg. Gated and direction-aware exactly
// like the alignment factor; unknown directions stay neutral (full credit).
export function downwindSwellRideWeight(
  windSpeed: number,
  windDirection: number | null,
  swellDirection: number | null,
): number {
  if (windSpeed < ALIGN_GATE_KMH) return 1.0;
  if (windDirection === null || swellDirection === null) return 1.0;
  const diff = angleDiff(windDirection, swellDirection);
  if (diff <= ALIGN_CROSS_DEG) return 1.0;
  return lerp(1.0, 0.0, (diff - ALIGN_CROSS_DEG) / (180 - ALIGN_CROSS_DEG));
}

// Swell's share of combined wave energy (audit 05 B4). The alignment penalty
// scales by this so a small background swell crossing a dominant wind sea is
// barely dented, while a swell that is at least half the combined energy
// (SHARE_FULL) takes the penalty in full: a dominant opposed swell confuses the
// whole sea, a 0.3m swell under 1.5m of wind sea barely registers.
const ALIGN_SHARE_FULL = 0.5;
export function downwindSwellEnergyShare(swellHeightM: number, windWaveHeightM: number): number {
  const s = swellHeightM * swellHeightM;
  const w = windWaveHeightM * windWaveHeightM;
  return s + w <= 0 ? 0 : s / (s + w);
}
function downwindAlignPenaltyScale(swellShare: number): number {
  return Math.min(1, swellShare / ALIGN_SHARE_FULL);
}

// Compose the opposed-swell correction (P7-2): the rideable-energy base from the
// ride-weighted swell + wind wave, and the energy-share-scaled alignment factor.
// Returns the raw (un-weighted) combined sea separately so safety flags still see
// the real water size (a big opposed swell is dangerous even if not rideable).
export function downwindSeaState(
  swellHeightM: number,
  windWaveHeightM: number,
  shelter: number,
  windSpeed: number,
  windDirection: number | null,
  swellDirection: number | null,
): { rawCombinedSeaM: number; rideableCombinedSeaM: number; alignment: number } {
  const rideWeight = downwindSwellRideWeight(windSpeed, windDirection, swellDirection);
  const shelteredSwell = swellHeightM * shelter;
  const shelteredWindWave = windWaveHeightM * shelter;
  const rawCombinedSeaM = Math.sqrt(shelteredSwell ** 2 + shelteredWindWave ** 2);
  const rideableCombinedSeaM = Math.sqrt((shelteredSwell * rideWeight) ** 2 + shelteredWindWave ** 2);
  const rawAlign = downwindAlignmentFactor(windSpeed, windDirection, swellDirection);
  const swellShare = downwindSwellEnergyShare(swellHeightM, windWaveHeightM);
  const alignment = 1 - (1 - rawAlign) * downwindAlignPenaltyScale(swellShare);
  return { rawCombinedSeaM, rideableCombinedSeaM, alignment };
}

// ---------------------------------------------------------------------------
// Factor 5: Gust consistency (0.85–1.0)
// ---------------------------------------------------------------------------

export function downwindGustFactor(sustained: number, gust: number): number {
  if (sustained <= GUST_GATE_KMH) return 1.0;
  const ratio = gust / sustained;
  if (ratio <= GUST_RATIO_OK) return 1.0;
  const fullFactor = ratio <= GUST_RATIO_HIGH
    ? lerp(1.0, GUST_PENALTY_MODERATE, (ratio - GUST_RATIO_OK) / (GUST_RATIO_HIGH - GUST_RATIO_OK))
    : GUST_PENALTY_HEAVY + (GUST_PENALTY_MODERATE - GUST_PENALTY_HEAVY) * Math.exp(-(ratio - GUST_RATIO_HIGH));
  // A ratio in light air is noisy. Introduce its effect over a shoulder,
  // rather than a quality step at the light-wind gate.
  return lerp(1, fullFactor, Math.min(1, (sustained - GUST_GATE_KMH) / 6));
}

// ---------------------------------------------------------------------------
// Factor 6: Current (0.55–1.0)
// ---------------------------------------------------------------------------

export function downwindCurrentFactor(
  velocity: number,
  currentTowardsDeg?: number | null,
  routeTowardsDeg?: number | null,
): number {
  if (currentTowardsDeg != null && routeTowardsDeg != null &&
      Number.isFinite(currentTowardsDeg) && Number.isFinite(routeTowardsDeg)) {
    const angle = (currentTowardsDeg - routeTowardsDeg) * Math.PI / 180;
    // With-run drift is not an enjoyment defect. Cross-run drift still
    // requires line correction; an opposing current demands full extra effort.
    velocity *= Math.hypot(Math.max(0, -Math.cos(angle)), 0.5 * Math.sin(angle));
  }
  if (velocity <= CURRENT_SAFE_MS) return 1.0;
  if (velocity <= CURRENT_NOTICEABLE_MS) return lerp(1.0, CURRENT_NOTICEABLE_FACTOR, (velocity - CURRENT_SAFE_MS) / (CURRENT_NOTICEABLE_MS - CURRENT_SAFE_MS));
  if (velocity <= CURRENT_STRONG_MS) return lerp(CURRENT_NOTICEABLE_FACTOR, CURRENT_STRONG_FACTOR, (velocity - CURRENT_NOTICEABLE_MS) / (CURRENT_STRONG_MS - CURRENT_NOTICEABLE_MS));
  return CURRENT_FLOOR + (CURRENT_STRONG_FACTOR - CURRENT_FLOOR) * Math.exp(-(velocity - CURRENT_STRONG_MS));
}

// ---------------------------------------------------------------------------
// Factor 7: Rain (0.65–1.0)
// ---------------------------------------------------------------------------

export function downwindRainFactor(precip: number): number {
  if (precip <= 0) return 1.0;
  if (precip <= RAIN_LIGHT_MM) return lerp(1.0, 0.93, precip / RAIN_LIGHT_MM);
  if (precip <= RAIN_MODERATE_MM) return lerp(0.93, 0.80, (precip - RAIN_LIGHT_MM) / (RAIN_MODERATE_MM - RAIN_LIGHT_MM));
  if (precip <= RAIN_HEAVY_MM) return lerp(0.80, RAIN_FLOOR, (precip - RAIN_MODERATE_MM) / (RAIN_HEAVY_MM - RAIN_MODERATE_MM));
  return RAIN_FLOOR;
}

// ---------------------------------------------------------------------------
// Factor 8: Visibility (0.35–1.0)
// ---------------------------------------------------------------------------

export function downwindVisibilityFactor(vis: number): number {
  if (vis >= VIS_CLEAR_M) return 1.0;
  if (vis >= VIS_REDUCED_M) return lerp(0.85, 1.0, (vis - VIS_REDUCED_M) / (VIS_CLEAR_M - VIS_REDUCED_M));
  if (vis >= VIS_POOR_M) return lerp(0.65, 0.85, (vis - VIS_POOR_M) / (VIS_REDUCED_M - VIS_POOR_M));
  if (vis >= VIS_DANGEROUS_M) return lerp(0.45, 0.65, (vis - VIS_DANGEROUS_M) / (VIS_POOR_M - VIS_DANGEROUS_M));
  return VIS_FLOOR;
}

// ---------------------------------------------------------------------------
// Labels - shared platform bands (0–10 daily score)
// ---------------------------------------------------------------------------

export function downwindFoilLabel(score: number): string {
  if (score >= 9.0) return "Hero";
  if (score >= 7.5) return "Firing";
  if (score >= 6.5) return "Great";
  if (score >= 5.0) return "Good";
  if (score >= 3.5) return "Fair";
  if (score >= 2.0) return "Poor";
  return "Flat";
}

// ---------------------------------------------------------------------------
// Safety indicators - independent of score
// ---------------------------------------------------------------------------

export type DownwindFoilSafetyIndicator = {
  key: string;
  level: "green" | "amber" | "red" | "unavailable";
  severity: "green" | "amber" | "red" | "unavailable";
  label: string;
  detail?: string;
  confidence?: "full" | "unavailable";
  provenance?: "missing-forecast-input";
};

export function downwindFoilSafetyIndicators(input: {
  weatherCode: number;
  combinedSeaM: number | null;
  windSpeed: number;
  gustSpeed: number | null;
  currentVelocity: number | null;
  visibility: number | null;
}): DownwindFoilSafetyIndicator[] {
  const indicators: DownwindFoilSafetyIndicator[] = [];

  if (input.weatherCode >= 95) {
    indicators.push({
      key: "downwind-lightning",
      level: "red",
      severity: "red",
      label: "Lightning risk",
      detail: "Thunderstorm detected. Do not go on the water. Always check local marine weather warnings.",
    });
  }

  if (input.combinedSeaM === null) {
    indicators.push({ key: "downwind-sea-state", level: "unavailable", severity: "unavailable", label: "Bump sea-state data unavailable", detail: "The wind-wave forecast is missing, so offshore bump size cannot be assessed.", confidence: "unavailable", provenance: "missing-forecast-input" });
  } else if (input.combinedSeaM > 5.0) {
    indicators.push({
      key: "downwind-extreme-seas",
      level: "red",
      severity: "red",
      label: "Extreme seas",
      detail: "Very heavy open-ocean conditions. Rescue is difficult offshore.",
    });
  } else if (input.combinedSeaM > 4.0) {
    indicators.push({
      key: "downwind-heavy-seas",
      level: "amber",
      severity: "amber",
      label: "Heavy seas",
      detail: "Experienced crews with boat support only.",
    });
  }

  if (input.gustSpeed === null) {
    indicators.push({ key: "downwind-gusts", level: "unavailable", severity: "unavailable", label: "Gust data unavailable", detail: "The gust forecast is missing, so offshore wind volatility cannot be assessed.", confidence: "unavailable", provenance: "missing-forecast-input" });
  } else if (input.gustSpeed > 60 || input.windSpeed > 45) {
    indicators.push({
      key: "downwind-dangerous-wind",
      level: "red",
      severity: "red",
      label: "Dangerous wind",
      detail: `Sustained ${Math.round(input.windSpeed)} km/h, gusts ${Math.round(input.gustSpeed)} km/h. Offshore runs are unforgiving in this.`,
    });
  }

  if (input.currentVelocity === null) {
    indicators.push({ key: "downwind-current", level: "unavailable", severity: "unavailable", label: "Current data unavailable", detail: "The current forecast is missing, so route drift cannot be assessed.", confidence: "unavailable", provenance: "missing-forecast-input" });
  } else if (input.currentVelocity > 1.0) {
    indicators.push({
      key: "downwind-current",
      level: "red",
      severity: "red",
      label: "Very strong current",
      detail: `Current ${input.currentVelocity.toFixed(1)} m/s. Plan the run with the current, never against it.`,
    });
  } else if (input.currentVelocity > 0.5) {
    indicators.push({
      key: "downwind-current",
      level: "amber",
      severity: "amber",
      label: "Strong current",
      detail: `Current ${input.currentVelocity.toFixed(1)} m/s. Factor drift into the run plan.`,
    });
  }

  if (input.visibility === null) {
    indicators.push({ key: "downwind-visibility", level: "unavailable", severity: "unavailable", label: "Visibility data unavailable", detail: "The visibility forecast is missing, so offshore navigation risk cannot be assessed.", confidence: "unavailable", provenance: "missing-forecast-input" });
  } else if (input.visibility < 500) {
    indicators.push({
      key: "downwind-visibility",
      level: "red",
      severity: "red",
      label: "Very poor visibility",
      detail: "Visibility below 500m. Navigation offshore is dangerous.",
    });
  } else if (input.visibility < 1000) {
    indicators.push({
      key: "downwind-visibility",
      level: "amber",
      severity: "amber",
      label: "Reduced visibility",
      detail: "Visibility below 1km. Stay within sight of landmarks.",
    });
  }

  return indicators;
}

// ---------------------------------------------------------------------------
// Hourly input / output types
// ---------------------------------------------------------------------------

export type DownwindFoilHourlyInput = {
  swellHeight: number;
  windWaveHeight: number;
  /**
   * Primary swell period (s). Revision 1 callers always pass a number (their own fallback is
   * 8 s); revision 2 passes null when the provider had none, and a null period takes the
   * neutral factor there (design 2.5).
   */
  period: number | null;
  /** Wind-sea period (s); read only by revision 2 (WFD-1: the bump period when wind sea dominates). */
  windWavePeriod?: number | null;
  /** Combined-sea period (s); read only by revision 2, as the last period fallback. */
  wavePeriod?: number | null;
  swellDirection: number | null;
  windSpeed: number;
  gustSpeed: number;
  windDirection: number | null;
  currentVelocity: number;
  currentDirection?: number | null;
  routeBearingDeg?: number | null;
  precipitation: number;
  visibility: number;
  weatherCode: number;
  shelterFactor?: number;
};

export type DownwindFoilHourlyResult = {
  score: number;
  label: string;
  safety: DownwindFoilSafetyIndicator[];
  factors: Readonly<{
    base: number;
    period: number;
    windSpeed: number;
    alignment: number;
    gust: number;
    current: number;
    rain: number;
    visibility: number;
  }>;
  readings: Readonly<{ rawCombinedSeaM: number; rideableCombinedSeaM: number }>;
};

// ---------------------------------------------------------------------------
// Hourly scoring
//
//   product = (energyBase(combined_sea) / 100)
//     × periodFactor × windSpeedFactor × alignmentFactor × gustFactor
//     × currentFactor × rainFactor × visibilityFactor
//
//   hourly_score = 100 × adaptiveGamma(product, MEDIUM_CHAIN)
//
// Note there is deliberately no chop penalty (wind waves feed the energy
// base), no tide factor (open water), and no shore-relative wind direction
// (runs are point-to-point, not spot-bound).
// ---------------------------------------------------------------------------

export function calculateDownwindFoilHourly(
  input: DownwindFoilHourlyInput,
  formula: DownwindFoilFormula = REVISION_ONE_FORMULA,
): DownwindFoilHourlyResult {
  // Revision 2 (WFD-1) runs its own branch; everything below is revision 1, unchanged.
  if (formula.revision >= 2) return calculateDownwindFoilHourlyRevisionTwo(input, formula);
  const shelter = input.shelterFactor ?? 1.0;
  // P7-2: opposed swell loses its rideable-energy credit (rideableCombinedSeaM
  // feeds the base) and the alignment penalty scales by swell share; the raw
  // combined sea still drives the safety flags.
  const { rawCombinedSeaM, rideableCombinedSeaM, alignment } = downwindSeaState(
    input.swellHeight,
    input.windWaveHeight,
    shelter,
    input.windSpeed,
    input.windDirection,
    input.swellDirection,
  );
  const combinedSeaM = rawCombinedSeaM;

  const base = downwindEnergyBase(rideableCombinedSeaM);
  const factors = Object.freeze({
    base: base / 100,
    period: downwindPeriodFactor(input.period ?? REVISION_ONE_DEFAULT_PERIOD_S),
    windSpeed: downwindWindSpeedFactor(input.windSpeed),
    alignment,
    gust: downwindGustFactor(input.windSpeed, input.gustSpeed),
    current: downwindCurrentFactor(input.currentVelocity, input.currentDirection,
      input.routeBearingDeg ?? (input.windDirection === null ? null : (input.windDirection + 180) % 360)),
    rain: downwindRainFactor(input.precipitation),
    visibility: downwindVisibilityFactor(input.visibility),
  });
  const raw = 100 * composeScore(DOWNWIND_FOIL_CHAIN, factors).compressed;
  const score = Math.round(Math.max(0, Math.min(100, raw)) * 10) / 10;

  const safety = downwindFoilSafetyIndicators({
    weatherCode: input.weatherCode,
    combinedSeaM,
    windSpeed: input.windSpeed,
    gustSpeed: input.gustSpeed,
    currentVelocity: input.currentVelocity,
    visibility: input.visibility,
  });

  return {
    score,
    label: downwindFoilLabel(score / 10),
    safety,
    factors,
    readings: Object.freeze({ rawCombinedSeaM, rideableCombinedSeaM }),
  };
}

// ---------------------------------------------------------------------------
// Daily derivation: 35% daylight average + 65% best 3h window
// (same blend as prone foil - a downwind day is built around one good run)
// ---------------------------------------------------------------------------

export function deriveDownwindFoilDaily(
  hourlyScores: { hour: number; score: number; isDay: boolean }[],
): { overall: number; bestWindowStart: number | null; bestWindowScores: number[] } {
  const daily = summariseDailyQuality(
    hourlyScores.map((point) => ({ ...point, isDay: point.isDay })),
    DAILY_BEST_WINDOW_HOURS, DAILY_BEST_WINDOW_WEIGHT,
  );
  return {
    overall: Math.round((daily.overall / 10) * 10) / 10,
    bestWindowStart: daily.bestWindowStart,
    bestWindowScores: daily.bestWindowScores,
  };
}

// ---------------------------------------------------------------------------
// Revision 2 (all-sports engine design 6.3; review WFD-1, owner decision 9)
//
// The trailing `formula` argument of calculateDownwindFoilHourly (engines/core/formula.ts
// convention): omitted or REVISION_ONE_FORMULA is revision 1, bit for bit; the downwind-foil
// revision 2 kernel (engines/coastal/r2/downwind-foil.ts) passes DOWNWIND_FOIL_R2_FORMULA.
//
// - windBand "knots": the wind band re-anchored in knots, where practitioners work (0.15 at
//   0-6 kn, 0.5 at 10, 0.8 at 13, 1.0 at 15-25, 0.9 at 28, 0.7 at 32, 0.4 at 38, then down to
//   the revision 1 floor of 0.2 by 45 kn). Revision 1 treated 15-25 km/h (8-13.5 kn) as prime
//   and glass as 0.90, so glassy groundswell outranked a prime 20 kn day.
// - swellCredit "wind-ramp": swell earns rideable-energy credit only as the wind builds, from
//   none at 10 kn to full at 15 kn; the wind sea always counts.
// - period "wind-sea": the bump period follows the wind sea when it is the larger partition,
//   then falls back to the other partitions; a period nobody reported is neutral (1.0).
// - unknownDirection "average": an unknown wind or swell direction takes the direction-averaged
//   factors (design 2.5), not revision 1's best-case neutral of 1.0, and an unknown current
//   direction (with a known speed) takes the current factor averaged over the current's
//   direction relative to the route, not revision 1's full-magnitude (head-on) current.
// Gust, current, rain and visibility neutrals are the kernel's inputs (gust = sustained, current
// 0, rain 0, visibility clear), so the chain and its gamma are those of revision 1.
// ---------------------------------------------------------------------------

/** Revision 1 callers' own period fallback (the route, backfill and enrichment pass `?? 8`). */
const REVISION_ONE_DEFAULT_PERIOD_S = 8;
const KMH_PER_KNOT = 1.852;
/** The direction grid of the averaged neutral: every 15 degrees (engines/core/missing-data.ts DIRECTION_AVERAGE_STEP_DEG). */
export const DOWNWIND_DIRECTION_AVERAGE_STEP_DEG = 15;

export type DownwindFoilFormula = LeafFormula<{
  windBand: "legacy" | "knots";
  swellCredit: "full" | "wind-ramp";
  period: "swell" | "wind-sea";
  unknownDirection: "neutral" | "average";
}>;

/** The downwind-foil revision 2 formula (hashed into the kernel's params). */
export const DOWNWIND_FOIL_R2_FORMULA = Object.freeze({
  revision: 2,
  windBand: "knots",
  swellCredit: "wind-ramp",
  period: "wind-sea",
  unknownDirection: "average",
} as const satisfies DownwindFoilFormula);

/** WFD-1 wind anchors: [knots, factor]. */
export const DOWNWIND_WIND_KNOT_ANCHORS: readonly (readonly [number, number])[] = Object.freeze([
  [0, 0.15], [6, 0.15], [10, 0.5], [13, 0.8], [15, 1.0], [25, 1.0], [28, 0.9], [32, 0.7], [38, 0.4], [45, 0.2],
] as const);

/** Swell credit ramp (knots): none at or below the first, full at or above the second. */
export const DOWNWIND_SWELL_CREDIT_KNOTS: readonly [number, number] = Object.freeze([10, 15] as const);

/**
 * Revision 2 period anchors [seconds, factor]: revision 1's curve (0.92 at 4 s, 0.99 at 8 s,
 * 1.02 from 11 s) with its exact 1.0 point at 9 s as a breakpoint, so the neutral stored period
 * (9 s) gives exactly the neutral factor.
 */
export const DOWNWIND_PERIOD_ANCHORS_R2: readonly (readonly [number, number])[] = Object.freeze([
  [4, 0.92], [8, 0.99], [9, 1.0], [11, 1.02],
] as const);

/** Piecewise-linear lookup that returns a breakpoint's value exactly at the breakpoint. */
function anchoredLookup(value: number, anchors: readonly (readonly [number, number])[]): number {
  if (value <= anchors[0][0]) return anchors[0][1];
  for (let i = 1; i < anchors.length; i++) {
    const [x1, y1] = anchors[i];
    if (value === x1) return y1;
    if (value < x1) {
      const [x0, y0] = anchors[i - 1];
      return y0 + ((value - x0) / (x1 - x0)) * (y1 - y0);
    }
  }
  return anchors[anchors.length - 1][1];
}

/** WFD-1 wind factor from a speed in km/h, anchored in knots. */
export function downwindWindSpeedFactorKnots(speedKmh: number): number {
  return anchoredLookup(speedKmh / KMH_PER_KNOT, DOWNWIND_WIND_KNOT_ANCHORS);
}

/** Share of the swell's rideable energy the wind has organised (0 at 10 kn, 1 at 15 kn). */
export function downwindSwellCredit(speedKmh: number): number {
  const knots = speedKmh / KMH_PER_KNOT;
  const [from, to] = DOWNWIND_SWELL_CREDIT_KNOTS;
  if (knots <= from) return 0;
  if (knots >= to) return 1;
  return (knots - from) / (to - from);
}

export function downwindPeriodFactorR2(periodS: number): number {
  return anchoredLookup(periodS, DOWNWIND_PERIOD_ANCHORS_R2);
}

/** Revision 1's sea state with the swell's rideable energy scaled by its wind credit. */
export function downwindSeaStateR2(
  swellHeightM: number,
  windWaveHeightM: number,
  shelter: number,
  windSpeed: number,
  windDirection: number | null,
  swellDirection: number | null,
  swellCredit: number,
): { rawCombinedSeaM: number; rideableCombinedSeaM: number; alignment: number } {
  const rideWeight = downwindSwellRideWeight(windSpeed, windDirection, swellDirection);
  const shelteredSwell = swellHeightM * shelter;
  const shelteredWindWave = windWaveHeightM * shelter;
  const rawCombinedSeaM = Math.sqrt(shelteredSwell ** 2 + shelteredWindWave ** 2);
  const rideableCombinedSeaM = Math.sqrt((shelteredSwell * rideWeight * swellCredit) ** 2 + shelteredWindWave ** 2);
  const rawAlign = downwindAlignmentFactor(windSpeed, windDirection, swellDirection);
  const alignment = 1 - (1 - rawAlign) * downwindAlignPenaltyScale(downwindSwellEnergyShare(swellHeightM, windWaveHeightM));
  return { rawCombinedSeaM, rideableCombinedSeaM, alignment };
}

function finitePeriod(value: number | null | undefined): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/** The bump period revision 2 scores: the wind sea's when it dominates, else the swell's, then fallbacks. */
export function downwindBumpPeriodR2(
  input: Pick<DownwindFoilHourlyInput, "swellHeight" | "windWaveHeight" | "period" | "windWavePeriod" | "wavePeriod">,
  source: "swell" | "wind-sea",
): number | null {
  const swell = finitePeriod(input.period);
  const windSea = finitePeriod(input.windWavePeriod);
  const combined = finitePeriod(input.wavePeriod);
  if (source === "wind-sea" && input.windWaveHeight > input.swellHeight) return windSea ?? swell ?? combined;
  return swell ?? windSea ?? combined;
}

const DIRECTION_GRID: readonly number[] = Object.freeze(
  Array.from({ length: 360 / DOWNWIND_DIRECTION_AVERAGE_STEP_DEG }, (_, index) => index * DOWNWIND_DIRECTION_AVERAGE_STEP_DEG),
);

/**
 * The current factor averaged over the current's direction relative to the route, on the
 * direction grid: revision 2's neutral for an unknown current direction.
 */
export function downwindCurrentFactorAveraged(velocity: number): number {
  let sum = 0;
  for (const relative of DIRECTION_GRID) sum += downwindCurrentFactor(velocity, relative, 0);
  return sum / DIRECTION_GRID.length;
}

function calculateDownwindFoilHourlyRevisionTwo(
  input: DownwindFoilHourlyInput,
  formula: DownwindFoilFormula,
): DownwindFoilHourlyResult {
  const shelter = input.shelterFactor ?? 1.0;
  const credit = formula.swellCredit === "wind-ramp" ? downwindSwellCredit(input.windSpeed) : 1;
  const average = formula.unknownDirection === "average";
  // The direction-dependent part: rideable energy (ride weight), alignment and the current's
  // route (it follows the wind without a route bearing). Averaged over an unknown direction.
  const windDirections = input.windDirection === null && average ? DIRECTION_GRID : [input.windDirection];
  const swellDirections = input.swellDirection === null && average ? DIRECTION_GRID : [input.swellDirection];
  // An unknown current direction: the factor averaged over every current direction relative to
  // the route (the same 15-degree grid), which no route or wind direction moves.
  const currentDirectionKnown = typeof input.currentDirection === "number" && Number.isFinite(input.currentDirection);
  const averagedCurrent = average && !currentDirectionKnown ? downwindCurrentFactorAveraged(input.currentVelocity) : null;
  let base = 0;
  let alignment = 0;
  let current = 0;
  let rideable = 0;
  let rawCombinedSeaM = 0;
  for (const windDirection of windDirections) {
    for (const swellDirection of swellDirections) {
      const sea = downwindSeaStateR2(input.swellHeight, input.windWaveHeight, shelter, input.windSpeed, windDirection, swellDirection, credit);
      base += downwindEnergyBase(sea.rideableCombinedSeaM) / 100;
      alignment += sea.alignment;
      rideable += sea.rideableCombinedSeaM;
      rawCombinedSeaM = sea.rawCombinedSeaM;
      current += averagedCurrent ?? downwindCurrentFactor(input.currentVelocity, input.currentDirection,
        input.routeBearingDeg ?? (windDirection === null ? null : (windDirection + 180) % 360));
    }
  }
  const samples = windDirections.length * swellDirections.length;
  const bumpPeriod = downwindBumpPeriodR2(input, formula.period === "wind-sea" ? "wind-sea" : "swell");
  const factors = Object.freeze({
    base: base / samples,
    period: bumpPeriod === null ? 1 : downwindPeriodFactorR2(bumpPeriod),
    windSpeed: formula.windBand === "knots" ? downwindWindSpeedFactorKnots(input.windSpeed) : downwindWindSpeedFactor(input.windSpeed),
    alignment: alignment / samples,
    gust: downwindGustFactor(input.windSpeed, input.gustSpeed),
    current: current / samples,
    rain: downwindRainFactor(input.precipitation),
    visibility: downwindVisibilityFactor(input.visibility),
  });
  const raw = 100 * composeScore(DOWNWIND_FOIL_CHAIN, factors).compressed;
  const score = Math.round(Math.max(0, Math.min(100, raw)) * 10) / 10;
  // The hourly result's safety field is the v1 policy, as for revision 1: safety is independent
  // of the score revision (design 2.7), and v2 rows come from downwindFoilSafetyV2.
  const safety = downwindFoilSafetyIndicators({
    weatherCode: input.weatherCode,
    combinedSeaM: rawCombinedSeaM,
    windSpeed: input.windSpeed,
    gustSpeed: input.gustSpeed,
    currentVelocity: input.currentVelocity,
    visibility: input.visibility,
  });
  return {
    score,
    label: downwindFoilLabel(score / 10),
    safety,
    factors,
    readings: Object.freeze({ rawCombinedSeaM, rideableCombinedSeaM: rideable / samples }),
  };
}

// ---------------------------------------------------------------------------
// Safety policy v2 (design 2.7 and 6.2; review WFD-1, WFD-5, WFD-6, WFD-8)
//
// Per-hour rows for the v2 safety module (engines/coastal/safety/downwind-foil.ts);
// downwindFoilSafetyIndicators above stays the v1 policy, byte for byte. Under v2:
// - the strong-wind ladder is anchored in knots: amber from 28 kn sustained, red from 32 kn
//   sustained (the conservative end of the approved 32-35 kn band) or 40 kn gusts on the water
//   (O-19), read on the modelled gust as it stands. v1 went red at 45 km/h sustained (24 kn) or
//   60 km/h gusts, flagging prime downwind days (WFD-1, WFD-8). The accelerated rollout Phase D
//   read the modelled (land-cell) gust against O-19's 40 kn as 45 kn, from the nearest Irish weather
//   buoys (Marine Institute M2 to M5, 9,869 paired hours), and withdrew it after review the same
//   day (gates addendum A14): the buoy anemometers stand 4.5 m above the sea (NDBC 62091 to 62094),
//   and with their 3-second gust raised to the 10 m height O-19 is expressed at (a neutral sea
//   profile, factor 1.06 to 1.07) the even chance of a 40 kn gust falls to a modelled 40.5 to 41.2 kn
//   (bootstrap 95% from about 39.6 to 42.3 kn), so the open-sea evidence keeps O-19's 40 kn. The 45 kn
//   reading caught 19 of the 160 open-sea red hours red against 51 at 40 kn;
// - wind against tide (wind-against-tide.ts, Phase D): on a curated run, a stream of 0.75 m/s or
//   more within 45 degrees of straight into a wind of 15 kn or more is red, and one of 0.5 m/s or
//   more against 12 kn or more amber, because an opposing stream steepens the wind sea the run
//   rides until it breaks;
// - a missing gust no longer suppresses the sustained flag (WFD-6);
// - a missing wind speed, weather code or sea state is never read as calm or clear (WFD-5),
//   and a known lower bound on the sea still flags heavy seas;
// - the current is an optional safety input (owner decision O-07): a missing current is the
//   neutral "no current" and adds no row, while the downwind score reads it as a modifier, so the
//   hour is partial there instead;
// - the current keeps v1's speeds and takes no along-coast relief (DOWNWIND_V2_CURRENT,
//   seaward-current.ts): amber above 0.5 m/s, red above 1.0 m/s whichever way it sets, as v1 read
//   it. The seaward component only words the red ("setting offshore"). The accelerated rollout
//   Phase C preparation first relieved a stream along a beach up to 2.0 m/s, as it does for the
//   surf-zone sports; that was withdrawn after review on 1 October 2026. A downwind run is ridden in
//   the stream itself, along a curated route, and the stream often runs against the route while
//   the run's own wind blows against it (wind against tide): tarifa's levante against a 1.25 to
//   1.61 m/s stream setting east, porthmeor's south-westerly against a stream setting south-west.
//   The relief read neither the route bearing nor the wind, so it removed the only red on those
//   days (H5: 9 of the 23 relieved beach-break days had 15 km/h or more of wind against a stream
//   above 1.0 m/s). Phase D adds the evidenced wind-against-tide row (above) beside it; the
//   speed alone still decides the current row. A missing current is still the neutral "no
//   current" (O-07).
// ---------------------------------------------------------------------------

/** Downwind foil current tiers in m/s (v1's), read on the speed alone (no along-coast relief; seaward-current.ts). */
export const DOWNWIND_V2_CURRENT = Object.freeze({ amberAboveMs: 0.5, redSeawardAboveMs: 1.0 });

/** Floating-point tolerance for knot thresholds (km/h to knots rounding), in knots. */
const DOWNWIND_KNOT_EPSILON = 1e-9;

/**
 * The v2 downwind strong-wind ladder, in knots: O-19's 40 kn red gust read on the modelled gust as
 * it stands (Phase D's 45 kn reading was withdrawn after review; see the section header).
 */
export const DOWNWIND_V2_WIND_LADDER_KN = Object.freeze({ amberSustained: 28, redSustained: 32, redGust: 40 });

function downwindAtLeastKnots(speedKmh: number, knots: number): boolean {
  return speedKmh / KMH_PER_KNOT >= knots - DOWNWIND_KNOT_EPSILON;
}

export type DownwindFoilSafetyV2Input = Readonly<{
  weatherCode: number | null;
  /** Sheltered raw combined sea (m) when every partition it needs is known, else null. */
  combinedSeaM: number | null;
  /** A lower bound on the combined sea from the known partitions, used when combinedSeaM is null. */
  combinedSeaMinM: number | null;
  windSpeed: number | null;
  gustSpeed: number | null;
  currentVelocity: number | null;
  /**
   * The current's seaward component in m/s (floored at 0), which only words the red row. Null or
   * omitted when unresolved: the whole speed then counts as seaward. Downwind runs take no
   * along-coast relief (see the section header), so the speed alone above 1.0 m/s is red.
   */
  currentSeawardMs?: number | null;
  /** Where the hour's wind blows from, in degrees (wind against tide); null or omitted when missing. */
  windDirectionDeg?: number | null;
  /** Where the hour's current flows to, in degrees (wind against tide); null or omitted when missing. */
  currentDirectionDeg?: number | null;
  /**
   * The same hour's stream speed (m/s) for the wind-against-tide row, where `currentVelocity` is
   * not that hour's (the interim day summary passes its daylight peak there). Omitted: the row
   * reads `currentVelocity`, as the engine's per-hour module does; null: no stream that hour.
   */
  windAgainstTideStreamMs?: number | null;
  /**
   * The curated run's route bearing in degrees. Null or omitted off a curated run: no
   * wind-against-tide row (wind-against-tide.ts).
   */
  routeBearingDeg?: number | null;
  visibility: number | null;
  /**
   * The hour's relative humidity in % (model-fog.ts), the visibility row's saturation test
   * (accelerated rollout Phase E). Null or omitted when unknown: the air counts as saturated, so a
   * missing humidity never lowers the row.
   */
  relativeHumidityPct?: number | null;
}>;

function downwindMissing(key: string, label: string, detail: string): DownwindFoilSafetyIndicator {
  return { key, level: "unavailable", severity: "unavailable", label, detail, confidence: "unavailable", provenance: "missing-forecast-input" };
}

function downwindRow(key: string, level: "amber" | "red", label: string, detail: string): DownwindFoilSafetyIndicator {
  return { key, level, severity: level, label, detail };
}

/** Per-hour downwind foil rows under safety policy v2. */
export function downwindFoilSafetyV2(input: DownwindFoilSafetyV2Input): DownwindFoilSafetyIndicator[] {
  const rows: DownwindFoilSafetyIndicator[] = [];
  const speed = input.windSpeed;
  const gust = input.gustSpeed;
  const ladder = DOWNWIND_V2_WIND_LADDER_KN;

  if (input.weatherCode === null) {
    rows.push(downwindMissing("downwind-lightning", "Storm forecast unavailable", "The weather-code forecast is missing, so thunderstorm risk cannot be assessed."));
  } else if (input.weatherCode >= 95) {
    rows.push(downwindRow("downwind-lightning", "red", "Lightning risk", "Thunderstorm detected. Do not go on the water. Always check local marine weather warnings."));
  }

  const sea = input.combinedSeaM ?? input.combinedSeaMinM;
  if (sea !== null && sea > 5.0) {
    rows.push(downwindRow("downwind-extreme-seas", "red", "Extreme seas", "Very heavy open-ocean conditions. Rescue is difficult offshore."));
  } else if (sea !== null && sea > 4.0) {
    rows.push(downwindRow("downwind-heavy-seas", "amber", "Heavy seas", "Experienced crews with boat support only."));
  } else if (input.combinedSeaM === null) {
    rows.push(downwindMissing("downwind-sea-state", "Bump sea-state data unavailable", "The sea-state forecast is incomplete, so offshore bump size cannot be assessed."));
  }

  if (gust === null) {
    rows.push(downwindMissing("downwind-gusts", "Gust data unavailable", "The gust forecast is missing, so offshore wind volatility cannot be assessed."));
  }
  const redSustained = speed !== null && downwindAtLeastKnots(speed, ladder.redSustained);
  const redGust = gust !== null && downwindAtLeastKnots(gust, ladder.redGust);
  if (redSustained || redGust) {
    rows.push(downwindRow("downwind-dangerous-wind", "red", "Dangerous wind",
      `${speed === null ? "Sustained wind unknown" : `Sustained ${Math.round(speed)} km/h`}${gust === null ? "" : `, gusts ${Math.round(gust)} km/h`}. Offshore runs are unforgiving in this.`));
  } else if (speed !== null && downwindAtLeastKnots(speed, ladder.amberSustained)) {
    rows.push(downwindRow("downwind-strong-wind", "amber", "Strong wind", `Sustained ${Math.round(speed)} km/h. Experienced riders with a planned exit only.`));
  } else if (speed === null && !(gust !== null && !downwindAtLeastKnots(gust, ladder.amberSustained))) {
    // The sustained wind never exceeds its gust, so a gust below the amber threshold clears the ladder.
    rows.push(downwindMissing("downwind-wind-data", "Wind data unavailable", "The wind forecast is missing, so overpowering cannot be assessed."));
  }

  // O-07: a missing current is the neutral "no current" (no row). No along-coast relief: a run is
  // ridden in the stream (DOWNWIND_V2_CURRENT; see the section header).
  if (input.currentVelocity !== null) {
    const current = classifySuppliedCurrent(input.currentVelocity, input.currentSeawardMs, {
      ...DOWNWIND_V2_CURRENT,
      alongCoastRelief: false,
    });
    const speed = input.currentVelocity.toFixed(1);
    if (current.level === "red") {
      rows.push(downwindRow("downwind-current", "red", "Very strong current", current.reason === "seaward" && current.resolved
        ? `Current ${speed} m/s, ${current.seawardMs.toFixed(1)} m/s of it setting offshore. Risk of being carried out to sea; do not run.`
        : `Current ${speed} m/s. Plan the run with the current, never against it.`));
    } else if (current.level === "amber") {
      rows.push(downwindRow("downwind-current", "amber", "Strong current", `Current ${speed} m/s. Factor drift into the run plan.`));
    }
  }

  // Wind against tide on a curated run (wind-against-tide.ts; see the section header).
  const againstTideStream = input.windAgainstTideStreamMs !== undefined ? input.windAgainstTideStreamMs : input.currentVelocity;
  const againstTide = classifyWindAgainstTide({
    routeBearingDeg: input.routeBearingDeg ?? null,
    windKmh: speed,
    windFromDeg: input.windDirectionDeg ?? null,
    gustKmh: gust,
    streamMs: againstTideStream,
    streamTowardsDeg: input.currentDirectionDeg ?? null,
  });
  if (againstTide.level === "red" || againstTide.level === "amber") {
    rows.push(downwindRow("downwind-wind-against-tide", againstTide.level, "Wind against tide",
      windAgainstTideDetail(againstTide, speed, againstTideStream as number)));
  } else if (againstTide.level === "unavailable") {
    rows.push(downwindMissing("downwind-wind-against-tide", "Wind against tide unavailable", "The wind forecast is missing, so a stream setting against the wind cannot be assessed."));
  }

  // Model fog (model-fog.ts, accelerated rollout Phase E): red only in saturated air.
  const fog = classifyModelFog({ visibilityM: input.visibility, relativeHumidityPct: input.relativeHumidityPct });
  if (fog === "unavailable") {
    rows.push(downwindMissing("downwind-visibility", "Visibility data unavailable", "The visibility forecast is missing, so offshore navigation risk cannot be assessed."));
  } else if (fog === "red") {
    rows.push(downwindRow("downwind-visibility", "red", "Very poor visibility", "Visibility below 500m. Navigation offshore is dangerous."));
  } else if (fog === "amber") {
    rows.push(downwindRow("downwind-visibility", "amber", "Reduced visibility", "Visibility below 1km. Stay within sight of landmarks."));
  }

  return rows;
}

export { isDark };
export type { SunTimes };
